/* eslint-disable */
import React, { useContext } from "react";
import { observer } from "mobx-react-lite";
import SelectedLink from "./core/views/SelectedLink";
import { DashboardWord, DataSourceWord, EnvironmentWord, IndexWord, OpenLakeWord, OutputWord } from "./core/Language";
import { ClustersLogo, DashboardLogo, IndexLogo, InputLogo, OutputLogo } from "./MainLogos";
import Drawer from "@mui/material/Drawer";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import { collapsedSidebarWidth, mainSidebarWidth } from "./styles/sizes";
import Box from "@mui/material/Box";
import { SettingsButton, UserSettingsButton } from "./TopBar";
// import { NotificationsMenu } from "./notifications/NotificationsPage";
import Divider from "@mui/material/Divider";
import { lightBlue } from "@mui/material/colors";
import { GlobalContext } from "./GlobalContext";
import { User } from "./core/api/contracts/User";
import { Organization } from "./core/api/contracts/Organization";
import { BetaIcon } from "./core/views/beta";
import { DocsLink } from "./sidebar";
import { useOnboardingData } from "./core/views/home-page/onboarding";
import IntegrateOrganization from "./auth/IntegrateFreeOrganization";
import HomeSidebarItem from "./HomeSidebarItem";
import UpsolverLogo from "./UpsolverLogo";
import { useLoginStateProvider } from "./LoginStateProvider";
import VersionSwapper from "./VersionSwapper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";

const SquareContent: React.FC<{ short?: boolean }> = ({ children, short }) => (
  <div className={classNames("square-content", { short })}>
    <div className="content">{children}</div>
  </div>
);
const SquareContentText: React.FC = ({ children }) => <span className="text">{children}</span>;

const outputPathSelector = (path: string) => path.startsWith("/outputs") && !path.includes("IndexOutputParameters");

const indexPathSelector = (path: string) => path.startsWith("/indexes") || path.includes("IndexOutputParameters");

type SideBarLinksProps = {
  org: Organization;
  user: User;
};

export const SidebarLinks = ({ org, user }: SideBarLinksProps) => (
  <React.Fragment>
    <li title={DataSourceWord.plural()}>
      <SelectedLink scope="/inputs" to="/inputs/list" automationName="to-inputs">
        <SquareContent>
          {InputLogo}
          Data
          <br />
          Sources
        </SquareContent>
      </SelectedLink>
    </li>
    {/*<li><SelectedLink scope="/monitoring" to="/monitoring">Dashboard</SelectedLink></li>*/}
    <li title={OutputWord.plural()}>
      <SelectedLink scope="/outputs" to="/outputs/list" selector={outputPathSelector} automationName="to-outputs-list">
        <SquareContent>
          {OutputLogo}
          <OutputWord.component plural />
        </SquareContent>
      </SelectedLink>
    </li>
    <li title={IndexWord.plural()}>
      <SelectedLink scope="/indexes" to="/indexes/list" selector={indexPathSelector}>
        <SquareContent>
          {IndexLogo}
          <IndexWord.component plural />
        </SquareContent>
      </SelectedLink>
    </li>
    {user && user.hasDashboards && (
      <li title={DashboardWord.plural()}>
        <SelectedLink scope="/dashboards" to="/dashboards/list" automationName="to-dashboards-list">
          <SquareContent>
            {DashboardLogo}
            <DashboardWord.component plural />
          </SquareContent>
        </SelectedLink>
      </li>
    )}
    {(org?.plan?.allowCreateEnvironments || org?.plan?.clazz === "FreePlan") && (
      <li title={EnvironmentWord.plural()}>
        <SelectedLink scope="/environments" to="/environments/list">
          <SquareContent>
            {ClustersLogo}
            <EnvironmentWord.component plural />
          </SquareContent>
        </SelectedLink>
      </li>
    )}
    {/*{org?.flags?.allowPrestoClusters && (*/}
    {/*  <li title={OpenLakeWord.single()}>*/}
    {/*    <SelectedLink scope="/openlake" to="/openlake/worksheets">*/}
    {/*      <SquareContent>*/}
    {/*        <OpenLakeWord.component />*/}
    {/*        <BetaIcon />*/}
    {/*      </SquareContent>*/}
    {/*    </SelectedLink>*/}
    {/*  </li>*/}
    {/*)}*/}
  </React.Fragment>
);

const useStyles: any = makeStyles((theme) => {
  return createStyles({
    drawer: {
      width: mainSidebarWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: mainSidebarWidth,
    },
    drawerOpen: {
      width: mainSidebarWidth,
    },
    drawerClose: {
      overflowX: "hidden",
      width: collapsedSidebarWidth,
    },
    smallDivider: {
      borderColor: lightBlue["600"],
      backgroundColor: lightBlue["600"],
      color: lightBlue["600"],
      width: 32,
      margin: `${theme.spacing(0.5)} auto`,
    },
  });
});
const OnboardingIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
    <path d="M288 288c13.25 0 24-14.33 24-32s-10.75-32-24-32-24 14.33-24 32 10.75 32 24 32zm344 192H512V96c0-17.67-14.33-32-32-32h-96V33.18C384 14.42 369.2 0 352.06 0c-2.57 0-5.19.32-7.83 1.01l-192 49.74C137.99 54.44 128 67.7 128 82.92V480H8c-4.42 0-8 3.58-8 8v16c0 4.42 3.58 8 8 8h624c4.42 0 8-3.58 8-8v-16c0-4.42-3.58-8-8-8zM352 33.18V480H160l.26-398.27 191.8-49.69-.06 1.14zM480 480h-96V96h96v384z" />
  </svg>
);

export const MainSidebar: React.FC<any> = observer((props) => {
  const classes = useStyles();
  const { auth, globalUIStore } = useContext(GlobalContext);
  const loginState = useLoginStateProvider();
  const { user } = auth;
  const { showLinks, showMore } = props;
  const onboarding = useOnboardingData();
  const drawerClass = globalUIStore.collapsed ? classes.drawerClose : classes.drawerOpen;
  const smallDivider = <Divider className={classes.smallDivider} />;
  const org = loginState.status?.selectedOrganization;
  const freePlan = org?.plan?.clazz === "FreePlan";
  // console.log(`user: ${user?.email}`, org);
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      open={true}
      className={classNames(classes.drawer, drawerClass)}
      classes={{ paper: drawerClass }}
    >
      <Box
        component="nav"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        className={classNames("main-sidebar", globalUIStore.collapsed && "collapsed", [classes.drawer], drawerClass)}
      >
        <span>
          {showLinks ? (
            <>
              <li>
                <HomeSidebarItem />
              </li>
              <IntegrateOrganization />
              {/*{freePlan && !onboarding?.hidden && (*/}
              {/*  <li>*/}
              {/*    <SelectedLink scope="/onboard" to="/onboard" automationName="to-onboard">*/}
              {/*      <SquareContent>*/}
              {/*        {OnboardingIcon}*/}
              {/*        Onboarding*/}
              {/*      </SquareContent>*/}
              {/*    </SelectedLink>*/}
              {/*  </li>*/}
              {/*)}*/}
              <SidebarLinks org={org} user={user} />
            </>
          ) : (
            <>
              <li>
                <UpsolverLogo />
              </li>
              <IntegrateOrganization />
            </>
          )}

          {user && auth.currentOrganization?.id && (
            <SquareContent>
              <SettingsButton user={user} org={org} showLinks={showLinks} />
              <SquareContentText>More</SquareContentText>
            </SquareContent>
          )}
        </span>
        <li>
          <Box width={globalUIStore.sidebarWidth} alignItems="center" display="flex" flexDirection="column">
            <DocsLink />
            {smallDivider}
            <IconButton
              title={"Contact Support"}
              size={"large"}
              onClick={() => {
                // the wizards need to be "shown" to open
                window.supportChat.show();
                window.supportChat.open();
                // subscribe to the close event of the chat to hide the widget again
                window.zE("webWidget:on", "close", () => window.supportChat.hide());
              }}
            >
              <HelpOutlineIcon style={{ color: "white" }} />
            </IconButton>
            {/*<NotificationsMenu />*/}
            {smallDivider}
            <UserSettingsButton showImpersonate={props.showImpersonate} />
            <VersionSwapper />
          </Box>
        </li>
      </Box>
    </Drawer>
  );
});
